import { Helmet, HelmetData } from "react-helmet-async";
import {
  AI_BG_NEW,
  Connect_Icon,
  Continous_Icon,
  Data_layer_Icon,
  Data_Speed_Icon,
  LSE_Logo_White,
  Our_Product_Bg,
} from "../../assets";
import { Link } from "react-router-dom";
import { useRef } from "react";
import gsap from "gsap";
import { useGSAP } from "@gsap/react";
import {
  MOTION_OK,
  revealTrigger,
  riseFrom,
  maskedLineReveal,
} from "../../utils/animations";
const helmetData = new HelmetData({});

const why = [
  {
    icon: Connect_Icon,
    title: "User-friendly interface",
    text: "An easy drop-down menu makes algorithmic strategies accessible to traders of every level.",
  },
  {
    icon: Data_Speed_Icon,
    title: "No-code environment",
    text: "A chat-based interface returns clear results, so you focus on the strategy, not the syntax.",
  },
  {
    icon: Data_layer_Icon,
    title: "Data-driven decisions",
    text: "Real market data underpins every run, keeping your simulated P&L grounded and accurate.",
  },
  {
    icon: Continous_Icon,
    title: "Continuous improvement",
    text: "Refine and optimise easily, evolving your approach as market conditions change.",
  },
];

export default function TradeAssistantPage() {
  const pageRef = useRef(null);

  useGSAP(
    () => {
      const mm = gsap.matchMedia();
      mm.add(MOTION_OK, () => {
        maskedLineReveal(pageRef.current.querySelector(".tt-hero-heading"), {
          scrollTrigger: revealTrigger(".tt-hero-heading"),
        });
        riseFrom(".tt-hero-item", {
          delay: 0.2,
          stagger: 0.12,
          scrollTrigger: revealTrigger(".tt-hero-item", "top 85%"),
        });
        riseFrom(".tt-hero-media", {
          y: 40,
          duration: 1.1,
          scrollTrigger: revealTrigger(".tt-hero-media", "top 85%"),
        });
        riseFrom(".tt-do-head > *", {
          stagger: 0.15,
          scrollTrigger: revealTrigger(".tt-do-head"),
        });
        riseFrom(".tt-do-card", {
          y: 48,
          duration: 1,
          stagger: 0.12,
          scrollTrigger: revealTrigger(".tt-do-list", "top 75%"),
        });
        riseFrom(".tt-do-media", {
          y: 40,
          duration: 1.1,
          scrollTrigger: revealTrigger(".tt-do-media"),
        });
        riseFrom(".tt-how-head > *", {
          stagger: 0.15,
          scrollTrigger: revealTrigger(".tt-how-head"),
        });
        riseFrom(".tt-how-card", {
          y: 48,
          duration: 1,
          stagger: 0.12,
          scrollTrigger: revealTrigger(".tt-how-grid", "top 75%"),
        });
        riseFrom(".tt-cta-head > *", {
          stagger: 0.15,
          scrollTrigger: revealTrigger(".tt-cta-head"),
        });
        riseFrom(".tt-cta-btn", {
          delay: 0.35,
          scrollTrigger: revealTrigger(".tt-cta-btn", "top 90%"),
        });
        riseFrom(".tt-cta-foot > *", {
          stagger: 0.15,
          scrollTrigger: revealTrigger(".tt-cta-foot", "top 85%"),
        });
      });
      return () => mm.revert();
    },
    { scope: pageRef }
  );

  return (
    <>
      <Helmet helmetData={helmetData}>
        <title>
          TradeTutor — Backtest Trading Strategies with LSE Data
        </title>
        <link
          rel="canonical"
          href="https://analyticsintelligence.com/tradetutor"
        />
        <meta
          name="description"
          content="TradeTutor is a no-code trading sandbox by Analytics Intelligence, developed with the London Stock Exchange. Build, backtest, and monitor simulated strategies in plain English using real LSE market data."
        />
        <meta
          name="keywords"
          content="TradeTutor, algorithmic trading simulator, backtesting sandbox, London Stock Exchange market data, LSE, no-code trading strategies, learn algorithmic trading, strategy backtesting, simulated trading, Analytics Intelligence"
        />
        <meta name="robots" content="index, follow" />
        <meta name="author" content="Analytics Intelligence Ltd" />
        <meta
          property="og:title"
          content="TradeTutor — Backtest Trading Strategies with LSE Data"
        />
        <meta
          property="og:description"
          content="A no-code sandbox developed with the London Stock Exchange — build, backtest, and monitor simulated trading strategies in plain English using real LSE market data."
        />
        <meta
          property="og:url"
          content="https://analyticsintelligence.com/tradetutor"
        />
        <meta property="og:type" content="website" />
        <meta
          property="og:image"
          content="https://analyticsintelligence.com/favicon.png"
        />
        <meta property="og:site_name" content="Analytics Intelligence" />
        <meta property="og:locale" content="en_GB" />
        <meta name="twitter:card" content="summary" />
        <meta name="twitter:site" content="@_AnalyticsIntel" />
        <meta
          name="twitter:title"
          content="TradeTutor — Backtest Trading Strategies with LSE Data"
        />
        <meta
          name="twitter:description"
          content="A no-code sandbox developed with the London Stock Exchange — build, backtest, and monitor simulated trading strategies in plain English using real LSE market data."
        />
        <meta
          name="twitter:image"
          content="https://analyticsintelligence.com/favicon.png"
        />
        <script type="application/ld+json">{`
          {
            "@context": "https://schema.org",
            "@type": "SoftwareApplication",
            "name": "TradeTutor — Trading Strategy Sandbox",
            "applicationCategory": "FinanceApplication",
            "operatingSystem": "Web",
            "provider": {
              "@type": "Organization",
              "name": "Analytics Intelligence Ltd",
              "url": "https://analyticsintelligence.com"
            },
            "description": "TradeTutor is an educational, no-code trading strategy sandbox by Analytics Intelligence, developed in partnership with the London Stock Exchange. Users build, backtest, and stress-test simulated strategies in plain English against real LSE market data. All results are generated from historical data for learning purposes.",
            "url": "https://analyticsintelligence.com/tradetutor",
            "featureList": ["No-code strategy building", "Backtesting with real LSE data", "Instant stress testing", "Custom risk criteria", "Educational trading simulation"]
          }
        `}</script>
      </Helmet>
      <main ref={pageRef}>
        {/* Hero Section */}
        <section
          style={{
            backgroundImage: `url("${AI_BG_NEW}")`,
            backgroundPosition: "center",
            backgroundSize: "cover",
          }}
          className="min-h-[100vh] md:min-h-[90vh] flex items-center"
        >
          <div className="container mx-auto w-full h-full flex flex-col md:flex-row justify-between items-center gap-16 md:gap-4 xl:gap-16  pt-[126px] md:pt-[96px] py-[96px]">
            <div className="flex basis-2/4 flex-col flex-wrap gap-[16px] items-center md:items-start">
              <div className="space-y-4 flex flex-col items-center md:items-start">
                <span className="tt-hero-item text-base bg-[#EFF0F71F] px-3 p-[6px] rounded-[100px] text-white">
                  Developed in partnership with the London Stock Exchange
                </span>
                <h2
                  className="tt-hero-heading text-[36px] md:text-[48px] text-center md:text-left font-extrabold leading-[1.3] md:leading-[58px] text-white"
                  dangerouslySetInnerHTML={{
                    __html: "Your backtesting & live signal sandbox.",
                  }}
                />
                <p className="tt-hero-item text-sm md:text-lg text-center md:text-left font-medium text-[#E3E3E3] leading-5">
                  Build, backtest and monitor simulated trading strategies in
                  one safe sandbox, powered by real LSE market data, in plain
                  English, with no coding required.
                </p>
              </div>

              <div className="tt-hero-item flex gap-4">
                <a
                  href="https://www.londonstockexchange.com/personal-investing/tools/tradetutor-strategy-simulator"
                  target="_blank"
                  rel="noreferrer"
                >
                  <button className="text-sm md:text-base font-semibold py-3 px-4 md:px-6 min-w-[170px] text-center btn-sweep btn-sweep-blue text-white rounded-[100px] gap-2">
                    Start a Simulation
                  </button>
                </a>
              </div>
            </div>
            <div className="basis-2/4 flex justify-end">
              <img
                className="tt-hero-media w-full"
                src="https://res.cloudinary.com/analytics-intelligence/image/upload/v1783599677/website/TT_Hero_lvwamp.png"
                alt="illustration"
              />
            </div>
          </div>
        </section>

        <section className="bg-[#010127] relative pb-[96px]">
          <div className="container relative flex flex-col gap-11 isolate">
            <div className="w-[150px] lg:w-[400px] h-[300px] bg-[#0073FF]/50 blur-[150px] absolute bottom-[20%] left-[20%] translate-x-[-50%]"></div>

            <div className="tt-do-head flex flex-col items-center text-center md:text-left z-[1000]">
              <h2 className="text-white font-extrabold text-[24px] md:text-[32px] text-center">
                What you can do
              </h2>

              <h3 className="text-lg md:text-2xl font-bold text-[#88BEFF] my-2">
                Everything you need to develop a strategy with conviction.
              </h3>

              <p className="text-white text-sm md:text-base max-w-[837px] text-center">
                Build with simple dropdowns and conversational prompts then
                pressure-test the idea before it ever meets a live market.
              </p>
            </div>
            <div className="flex flex-col-reverse md:flex-row items-center mt-0 md:mt-11 gap-8">
              <div className="tt-do-list basis-full md:basis-[40%] lg:basis-[30%] flex flex-col gap-y-[32px] lg:max-w-[590px]">
                <div className="tt-do-card bg-[#40406C80] backdrop-blur-sm p-4 rounded-xl">
                  <h4 className="text-xl font-extrabold text-white">
                    Unleash strategies
                  </h4>
                  <p className="text-sm md:text-base text-white">
                    Use simple dropdowns and conversational prompts to create,
                    test and improve algorithmic trading strategies, no code in
                    sight.
                  </p>
                </div>
                <div className="tt-do-card bg-[#40406C80] backdrop-blur-sm p-4 rounded-xl">
                  <h4 className="text-xl font-extrabold text-white">
                    Back test real data
                  </h4>
                  <p className="text-sm md:text-base text-white">
                    Simulate historic performance across hundreds of instruments
                    using real market data from the London Stock Exchange.
                  </p>
                </div>
                <div className="tt-do-card bg-[#40406C80] backdrop-blur-sm p-4 rounded-xl">
                  <h4 className="text-xl font-extrabold text-white">
                    Stress test instantly
                  </h4>
                  <p className="text-sm md:text-base text-white">
                    Read key metrics immediately to gauge how robust a strategy
                    stays under shifting market conditions.
                  </p>
                </div>
                <div className="tt-do-card bg-[#40406C80] backdrop-blur-sm p-4 rounded-xl">
                  <h4 className="text-xl font-extrabold text-white">
                    Tailor risk criteria
                  </h4>
                  <p className="text-sm md:text-base text-white">
                    Shape simulations around your own risk tolerance, so what
                    you test reflects how you'd trade.
                  </p>
                </div>
              </div>

              <div className="flex-1 flex items-center justify-center z-50">
                <video
                  src="https://res.cloudinary.com/analytics-intelligence/video/upload/v1783580824/website/TT_Video_for_Website_lscfui.mp4"
                  alt="Data Structures"
                  className="tt-do-media w-full h-auto"
                  autoPlay
                  loop
                  muted
                  playsInline
                />
              </div>
            </div>
          </div>
        </section>

        <section className="bg-[#EDF1FF] py-[48px]">
          <div className="container space-y-12">
            <div className="tt-how-head">
              <h2 className="text-[#020128] font-extrabold text-[24px] md:text-[32px] text-center">
                How TradeTutor works 
              </h2>
              <p className="text-[#020128] text-sm md:text-base text-center">
                From a question to a tested strategy, in four steps. 
              </p>
            </div>

            <div className="tt-how-grid grid grid-cols-12 gap-8">
              {why.map((item, idx) => (
                <div
                  key={idx}
                  className="tt-how-card group col-span-12 md:col-span-6 bg-white hover:bg-poppingBlue flex flex-col md:flex-row items-center md:items-start rounded-xl p-6 md:p-8 gap-4 transition-colors duration-700"
                >
                  <img
                    src={item.icon}
                    alt={`${item.title} illustration`}
                    className="w-16 h-16 transition-all duration-700 group-hover:brightness-0 group-hover:invert"
                  />
                  <div className="space-y-2 text-center md:text-start">
                    <h5 className="text-base md:text-xl font-bold text-[#020128] transition-colors duration-700 group-hover:text-white">
                      {item.title}
                    </h5>
                    <p className="text-sm md:text-base text-[#020128] transition-colors duration-700 group-hover:text-white">
                      {item.text}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section
          className="relative pt-[116px] pb-10"
          style={{
            backgroundImage: `url("${Our_Product_Bg}")`,
            backgroundPosition: "center",
            backgroundSize: "cover",
            backgroundRepeat: "no-repeat",
          }}
        >
          <div className="container">
            <div className="bg-[#1E1E4080] backdrop-blur-[20px] flex flex-col gap-12 md:gap-6 px-4 md:px-8 lg:px-12 py-8 sm:py-12 md:py-14 lg:py-16 xl:py-20 rounded-3xl">
              <div className="flex flex-col items-center gap-6">
                <div className="tt-cta-head">
                  <h2 className="text-[32px] md:text-[44px] text-center font-extrabold text-white leading-[38px] mb-3">
                    No code. No risk. <br className="block md:hidden" />
                    Real data.
                  </h2>

                  <h4 className="text-[16px] md:text-[24px] text-center font-semibold text-white !mb-0">
                    Start learning algorithmic trading today.
                  </h4>

                  <p className="text-sm md:text-base text-center text-[#FFFFFF] max-w-[680px]">
                    Build your first simulated strategy in minutes.
                  </p>
                </div>

                <a
                  className="tt-cta-btn"
                  href="https://www.londonstockexchange.com/personal-investing/tools/tradetutor-strategy-simulator"
                  target="_blank"
                  rel="noreferrer"
                >
                  <button className="text-sm md:text-base font-semibold py-3 px-4 md:px-6 min-w-[170px] text-center btn-sweep btn-sweep-blue text-white rounded-[100px] gap-2">
                    Start a Simulation
                  </button>
                </a>
              </div>

              <div className="tt-cta-foot flex flex-col-reverse md:flex-row gap-6 md:gap-0 items-center justify-between">
                <div className="lg:max-w-[580px] space-y-3 text-center md:text-left">
                  <h2 className="text-[24px] md:text-[32px] font-extrabold text-white">
                    TradeTutor
                  </h2>
                  <div>
                    <p className="text-sm md:text-base text-[#C0C0C0] max-w-[680px]">
                      [AI] Analytics Intelligence in partnership with the London
                      Stock Exchange.
                    </p>
                    <p className="text-sm md:text-base text-[#C0C0C0] max-w-[680px]">
                      TradeTutor is an educational simulation tool. All results
                      are generated from historical data for learning purposes. 
                    </p>
                  </div>
                </div>

                <a
                  href="https://www.londonstockexchange.com/personal-investing/tools/tradetutor-strategy-simulator"
                  target="_blank"
                  rel="noreferrer"
                >
                  <img src={LSE_Logo_White} alt="LSE_Logo_White" />
                </a>
              </div>
            </div>
          </div>
        </section>
      </main>
    </>
  );
}
